/*
 *  Copyright (c) Qwoach LLC 2023. All rights reserved.
 *  Unauthorized copying of this file, via any medium is strictly prohibited
 *  Proprietary and confidential
 *  Created by Artem Bogush <artem@qwoach.com> on 6/11/2023
 */
import BaseObject from "@shared/BaseObject.js";
import {getDefaultPermissionsForRoles, getRoleObjByValue, RoleEnum} from "@shared/Permissions/RoleEnum.js";
import {
	AccessLevelArrayAll,
	ClearanceLevelEnum,
	getGrantedAccessLevelObject,
	getRequiredAccessLevelObject
} from "@shared/Profile/ClearanceLevelEnum.js";
import AccessScopeItem from "@shared/Permissions/AccessScopeItem.js";
import {
	AccessScopeEnum,
	ClientScopeAll, JournalScopeAll,
	ResourceScopeAll,
	SchedulerScopeAll
} from "@shared/Permissions/PermissionsEnum.js";

export default class AccessRuleItem extends BaseObject {

	// WARNING: Don't add too much data here
	// This class is synced to User CustomClaims
	// and claims are limited by 1000 bytes only.
	// Keep in mind, one user can be part of multiple owners,
	// so this may grow quickly.

	// this class synced to CustomClaims in zipped format.
	// AccessRules.toCustomClaims performs zipping before writing to CustomClaims; the source stays unchanged.
	// for zipping, the following fields are mapped to single letter keys:
	// let keyZipMapping = {
	//  myIdForOwner: "i",
	//  roles: 'r',
	// 	customPermissions: 'p',
	// 	clearanceLevel: 'c',
	// }

	myIdForOwner: string = null // BaseProfile id in owner account. If user has multiple roles, the id must be te same for each profile type, ie ClientProfile.id === TeamMemberProfile.id. This is enforced though acceptInvite flow on server
	roles: string[] = [];
	customPermissions: string[] = []
	clearanceLevel: ClearanceLevelEnum = ClearanceLevelEnum.STANDARD


	constructor(roles, scopes, accessLevel) {
		super();
		this.setRoles(roles);
		this.setScopes(scopes);
		this.setAccessLevel(accessLevel);
	}

	addRoles(roles){
		if(roles && !Array.isArray(roles)){
			roles = [roles]
		}
		for(let role of roles){
			let index = this.roles.indexOf(role)
			if(index < 0){
				this.roles.push(role)
			}
		}
	}

	setRoles(roles){
		if(roles && !Array.isArray(roles)){
			roles = [roles]
		}
		this.roles = roles || [];
	}

	setScopes(scopes){
		scopes = scopes || {}
		if(scopes.clients === AccessScopeEnum.ALL){
			// we grant both client and journal access so the member gets access to the journal entries of the client as well
			this.customPermissions.pushUnique(ClientScopeAll)
			this.customPermissions.pushUnique(JournalScopeAll)
		} else {
			this.customPermissions.deleteItem(ClientScopeAll)
			this.customPermissions.deleteItem(JournalScopeAll)
		}
		if(scopes.resources === AccessScopeEnum.ALL){
			this.customPermissions.pushUnique(ResourceScopeAll)
		} else {
			this.customPermissions.deleteItem(ResourceScopeAll)
		}
		if(scopes.schedulers === AccessScopeEnum.ALL){
			this.customPermissions.pushUnique(SchedulerScopeAll)
		} else {
			this.customPermissions.deleteItem(SchedulerScopeAll)
		}
	}
	getScopes(){
		let scope = new AccessScopeItem()
		if (this.customPermissions.includes(ClientScopeAll)) {
			scope.clients = AccessScopeEnum.ALL
		}
		if (this.customPermissions.includes(ResourceScopeAll)) {
			scope.resources = AccessScopeEnum.ALL
		}
		if (this.customPermissions.includes(SchedulerScopeAll)) {
			scope.schedulers = AccessScopeEnum.ALL
		}
		return scope
	}

	hasPermissionClientScopeAll(){
		return this.customPermissions.includes(ClientScopeAll)
	}



	setAccessLevel(accessLevel){
		this.clearanceLevel = accessLevel || ClearanceLevelEnum.STANDARD;
	}

	getAccessLevel(){
		return this.clearanceLevel;
	}


	hasAccessLevel(){
		return AccessLevelArrayAll.filter(item => this.clearanceLevel >= item)
	}
	noAccessLevel(){
		return AccessLevelArrayAll.filter(item => this.clearanceLevel < item)
	}
	hasAccessLevelFormatted(){
		return this.hasAccessLevel().map(accessLevel => getRequiredAccessLevelObject(accessLevel).label).join(' or ')
	}
	noAccessLevelFormatted(){
		return this.noAccessLevel().map(accessLevel => getRequiredAccessLevelObject(accessLevel).label).join(' or ')
	}

	accessLevelFormatted(){
		return getGrantedAccessLevelObject(this.clearanceLevel).labelShort
	}
	accessLevelTooltip(){
		return getGrantedAccessLevelObject(this.clearanceLevel).tooltip
	}
	grantedAccessLevelsFormattedAll(){
		let has = this.hasAccessLevel().map(accessLevel => getGrantedAccessLevelObject(accessLevel).label)
		let no = this.noAccessLevel().map(accessLevel => getGrantedAccessLevelObject(accessLevel).label)
		let hasAccess = has.length ? `access level ${has.join(", ")}` : null
		let noAccess = no.length ? `no access to ${no.join(", ")}` : null
		return [hasAccess, noAccess].filter(item => !!item).join(' but ')
	}

	isScopeClientsAll(){
		return this.customPermissions.includes(ClientScopeAll)
		// return this.scopes.clients === AccessScopeEnum.ALL;
	}

	isScopeResourcesAll(){
		return this.customPermissions.includes(ResourceScopeAll)
		// return this.scopes.resources === AccessScopeEnum.ALL;
	}

	isScopeSchedulersAll(){
		return this.customPermissions.includes(SchedulerScopeAll)
		// return this.scopes.schedulers === AccessScopeEnum.ALL;
	}

	getRoles(){
		return this.roles;
	}

	isCoach(){
		return this.roles.includes(RoleEnum.COACH);
	}
	isAdmin(){
		return this.roles.includes(RoleEnum.ADMIN);
	}
	isClient(){
		return this.roles.includes(RoleEnum.CLIENT);
	}

	getRolesFormatted(){
		return this.roles.map(role => getRoleObjByValue(role).label).join(', ');
	}

	getPermissions(){
		let defaultPermissions = getDefaultPermissionsForRoles(this.roles)
		return [...this.customPermissions, ...defaultPermissions]
	}

}
